import { jsx, jsxs } from 'react/jsx-runtime';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import ButtonIcon from '../ButtonIcon/index.js';
import { Icon } from '../Icon/icon.js';
import Label from '../Label/index.js';

const Input = React.forwardRef(
  ({
    RightContainer,
    className,
    containerClassName,
    defaultValue,
    displayError,
    focus,
    hasClearButton,
    hasSpellCheck,
    id,
    isAutoCompleteEnabled = true,
    isDisabled,
    isFilter,
    isOptional,
    isReadOnly,
    isRequired,
    label,
    leftIcon,
    leftIconName,
    max,
    maxLength,
    min,
    note,
    onBlur,
    onChange,
    onClearButtonClick,
    onClick,
    onFocus,
    onInput,
    onKeyDown,
    placeholder,
    rightIcon,
    rightIconName,
    step,
    testSection,
    textAlign,
    title,
    type,
    value,
    ...props
  }, ref) => {
    const renderNote = useCallback(
      () => /* @__PURE__ */ jsx(
        "div",
        {
          className: "oui-form-note",
          "data-test-section": testSection && testSection + "-note",
          children: note
        }
      ),
      [note, testSection]
    );
    const renderIcon = (iconName) => {
      return /* @__PURE__ */ jsx(Icon, { name: iconName, size: "small" });
    };
    const renderIconFromImport = (icon) => {
      return /* @__PURE__ */ jsx(Icon, { icon, size: "small" });
    };
    const renderClearButton = () => {
      return /* @__PURE__ */ jsx(
        ButtonIcon,
        {
          iconName: "xmark",
          onClick: onClearButtonClick,
          size: "small",
          style: "plain",
          title: "Clear Input"
        }
      );
    };
    let isValidInput = true;
    if (type === "number" && value && min && max) {
      const valueAsNumber = Number(value);
      isValidInput = valueAsNumber <= max && valueAsNumber >= min;
    }
    const showError = displayError || !isValidInput;
    const renderInput = () => {
      let hasAlignStyle = false;
      if (textAlign) {
        hasAlignStyle = true;
      }
      const classes = classNames(
        "oui-text-input",
        { "oui-text-input--read-only": isReadOnly },
        { "oui-text-input--search": isFilter },
        { "oui-form-bad-news": showError },
        { [`text--${textAlign}`]: hasAlignStyle },
        className
      );
      const input = /* @__PURE__ */ jsx(
        "input",
        {
          autoComplete: isAutoCompleteEnabled ? "on" : "off",
          className: classes,
          "data-oui-component": true,
          defaultValue,
          disabled: isDisabled,
          id,
          max,
          min,
          onBlur,
          onChange,
          onClick,
          onFocus,
          onInput,
          onKeyDown,
          placeholder,
          readOnly: isReadOnly,
          ref,
          required: isRequired,
          spellCheck: hasSpellCheck,
          step,
          title: title || void 0,
          type,
          value,
          ...typeof maxLength === "undefined" ? {} : { maxLength },
          autoFocus: focus,
          "data-test-section": testSection,
          ...props
        }
      );
      if (leftIconName || leftIcon || !!RightContainer || rightIconName || rightIcon || hasClearButton) {
        const containerClasses = classNames(
          containerClassName,
          "position--relative",
          {
            "oui-text-input-with-icon--left": leftIconName || leftIcon,
            "oui-text-input-with-icon--right": rightIconName || rightIcon || !!RightContainer || hasClearButton
          }
        );
        return /* @__PURE__ */ jsxs("div", { className: containerClasses, children: [
          leftIconName && /* @__PURE__ */ jsx("span", { className: "flex oui-input-icon__left", children: renderIcon(leftIconName) }),
          leftIcon && /* @__PURE__ */ jsx("span", { className: "flex oui-input-icon__left", children: renderIconFromImport(leftIcon) }),
          input,
          rightIconName && !hasClearButton && /* @__PURE__ */ jsx("span", { className: "flex oui-input-icon__right", children: renderIcon(rightIconName) }),
          rightIcon && !hasClearButton && /* @__PURE__ */ jsx("span", { className: "flex oui-input-icon__right", children: renderIconFromImport(rightIcon) }),
          !!RightContainer && !hasClearButton && /* @__PURE__ */ jsx("span", { className: "flex oui-text-input__right-container", children: /* @__PURE__ */ jsx(RightContainer, {}) }),
          hasClearButton && /* @__PURE__ */ jsx("span", { className: "flex oui-text-input__clear-button", children: renderClearButton() })
        ] });
      }
      return input;
    };
    if (label) {
      return /* @__PURE__ */ jsxs(
        "div",
        {
          className: classNames({ "oui-form-bad-news": showError }),
          "data-oui-component": true,
          children: [
            /* @__PURE__ */ jsx(
              Label,
              {
                inputId: id,
                isOptional,
                isRequired,
                testSection: testSection && testSection + "-label",
                children: label
              }
            ),
            renderInput(),
            note && renderNote()
          ]
        }
      );
    }
    return /* @__PURE__ */ jsxs(React.Fragment, { children: [
      renderInput(),
      note && renderNote()
    ] });
  }
);
Input.propTypes = {
  /** Id to link label and input for accessibility reasons
   *  @param {Object} props Object of props
   *  @returns {Error} Error or null
   */
  id: function verifyIDProp(props) {
    if (props.label && !props.id) {
      return new Error(
        "Inputs must include an id when a label is specified for accessibility purposes."
      );
    }
    return null;
  },
  /** Adds an optional label if there is a label provided
   *  @param {Object} props Object of props
   *  @returns {Error} Error or null
   */
  isOptional: function verifyIsOptionalProp(props) {
    if (props.isOptional && !props.label) {
      return new Error(
        "Must include a value for the label prop to use the isOptional prop"
      );
    }
    return null;
  },
  /** Includes required asterisk label if true
   *  @param {Object} props Object of props
   *  @returns {Error} Error or null
   */
  isRequired: function verifyIsRequiredProp(props) {
    if (props.isRequired && !props.label) {
      return new Error(
        "Must include a value for the label prop to use the isRequired prop"
      );
    }
    return null;
  }
};
Input.displayName = "Input";
Input.defaultProps = {
  hasSpellCheck: true,
  isRequired: false,
  note: null
};

export { Input as default };
